import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { Grid, Container } from "@gumtree/ui-library";
import {
    mediaQuery,
    breakpoints,
    gutterSizes,
    colorVariables,
    colors,
    boxSizes,
    boxShadow,
    fontSizes,
} from "@gumtree/ui-library/src/base/theme";

export const newLayout = css`
    border-radius: ${boxSizes.borderRadius};
    box-shadow: ${boxShadow.default};
    background-color: ${colors.white};
    padding: ${gutterSizes.base};
    box-sizing: border-box;
    border: none;
    width: auto;
    margin: ${gutterSizes.base};
`;

interface IsMobileCardsLayout {
    isMobileCardsLayout?: boolean;
}

const doNotForward = {
    shouldForwardProp: (p: string) => !["isMobileCardsLayout", "isMCUFeed"].includes(p),
};

export const AdBreadcrumbSlot = styled.div``;

interface VipContainerProps {
    isMobileCardsLayout?: boolean;
}

export const VipContainer = styled(Container, doNotForward)<VipContainerProps>`
    ${({ isMobileCardsLayout }) =>
        isMobileCardsLayout
            ? css`
                  .grid--item {
                      padding: 0;

                      &:first-of-type {
                          height: 0;
                      }
                  }
                  padding: 0;
              `
            : css`
                  #mpu {
                      /** Hide MPU on desktop < medium */
                      ${mediaQuery.until(breakpoints.medium)} {
                          display: none;
                      }
                  }
              `}
`;

export const VipContent = styled.main<{ isMobileCardsLayout?: boolean, forceShowBreadcrumbs?: boolean }>`
    background-color: ${({ isMobileCardsLayout }) =>
        isMobileCardsLayout ? colorVariables.backgroundLighter : colorVariables.backgroundNeutral};
    position: relative;
    clear: both;
    padding-top: ${gutterSizes.large};
    /* stylelint-disable declaration-block-no-shorthand-property-overrides */
    padding: ${({ isMobileCardsLayout }) => (isMobileCardsLayout ? 0 : null)};
    /* stylelint-enable */
    & .breadcrumbs-container {
        display: ${({ forceShowBreadcrumbs }) => (forceShowBreadcrumbs ? 'block' : 'none')};

        ${mediaQuery.from(breakpoints.medium)} {
            display: block;
            padding: ${gutterSizes.large} 0;
        }

        ${mediaQuery.from(breakpoints.small)} {
            margin-left: ${gutterSizes.base};
            margin-right: ${gutterSizes.base};
        }
    }

    & .grid--container {
        margin: 0;
    }

    #vipMiddleMobile,
    #textLinkBase {
        ${mediaQuery.from(breakpoints.small)} {
            &.ad-slot {
                :before {
                    content: none;
                }

                :after {
                    content: none;
                }
            }
        }
    }

    h1,
    h2,
    h3,
    h4,
    h5 {
        color: ${colorVariables.textPrimary};
    }
`;

export const GridContent = styled.div`
    ${mediaQuery.until(breakpoints.large)} {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
    }
`;

export const RightPanel = styled(Grid)<IsMobileCardsLayout & { isDesktop: boolean }>`
    ${({ isDesktop }) =>
        isDesktop &&
        css`
            ${mediaQuery.from(breakpoints.medium)} {
                float: right;
            }
        `}

    ${({ isMobileCardsLayout }) =>
        isMobileCardsLayout &&
        css`
            padding: 0;
        `}
`;

export const ButtonGroup = styled.div`
    display: flex;
    position: relative;
    padding-top: ${gutterSizes.xlarge};
    border-top: 1px solid ${colorVariables.borderLight};

    > button {
        flex: 1;
        justify-content: center;

        > span.icon {
            position: unset;
        }
    }

    .toggle {
        flex: 1;

        > button {
            width: 100%;
        }
    }

    .content {
        position: absolute;
        left: 0;
        right: 0;
        margin-top: 12px;
        padding: 12px;
        border: 1px solid gray;
    }

    & .rotate-toggle {
        transform: rotate(180deg);
    }
`;

export const VipAdArea = styled.div`
    float: left;
    vertical-align: top;
    width: 66%;

    ${mediaQuery.between(breakpoints.xsmall, breakpoints.large)} {
        width: 100%;
    }
`;

export const VipAdInfo = styled.section<{ isDesktop: boolean }>`
    color: ${colors.bark};

    ${mediaQuery.between(breakpoints.medium, breakpoints.large)} {
        flex: 1;
    }

    ${mediaQuery.from(breakpoints.medium)} {
        .attributes-group table.attributes-table {
            margin-bottom: ${gutterSizes.xxlarge};
        }
    }

    ${({ isDesktop }) =>
        isDesktop &&
        `
            ${mediaQuery.from(breakpoints.large)} {
                display: flex;
                flex-wrap: wrap;
                width: 100%;

                .attributes-group {
                    flex: 1 1 50%;
                    min-width: 256px;
                    flex-basis: 100%;

                    @media (min-width: 512px) {
                        max-width: 50%;
                    }

                    table.attributes-table {
                        width: 90%;
                    }
                }
            }
        `}
`;

export const VipAdSocial = styled.div`
    padding: ${gutterSizes.medium} 0;
    border-top: 1px solid ${colorVariables.borderLight};
    border-bottom: 1px solid ${colorVariables.borderLight};
    margin: ${gutterSizes.base} 0;
    width: 100%;
`;

export const SocialPanelRight = styled.div`
    align-self: flex-start;

    ${mediaQuery.until(breakpoints.medium)} {
        display: none !important;
    }
`;

export const SocialPanelLeft = styled(Grid)`
    align-self: flex-start;

    ${mediaQuery.from(breakpoints.medium)} {
        display: none !important;
    }
`;

export const VipAdCarousel = styled.div`
    ${mediaQuery.between(breakpoints.medium, breakpoints.large)} {
        padding-left: ${gutterSizes.large};
    }
`;

export const SellerInfoBlock = styled(RightPanel)`
    ${mediaQuery.until(breakpoints.large)} {
        display: flex;
        order: 1;
        flex-wrap: wrap;
    }

    ${mediaQuery.between(breakpoints.medium, breakpoints.large)} {
        width: 50%;
        order: 2;
        align-self: end;
    }

    ${mediaQuery.from(breakpoints.medium)} {
        padding-left: ${gutterSizes.large};
    }
`;

export const SellerInfoBlockContainer = styled.div<IsMobileCardsLayout>`
    border: 1px solid ${colors.bark20};
    box-shadow: 0px 2px 8px 0px ${colorVariables.borderLight};
    width: 100%;

    ${mediaQuery.until(breakpoints.large)} {
        margin: ${gutterSizes.large} 0;
    }

    ${({ isMobileCardsLayout }) =>
        isMobileCardsLayout &&
        css`
            ${newLayout}

            ${mediaQuery.until(breakpoints.medium)} {
                margin: ${gutterSizes.base};
                padding: ${gutterSizes.small};
            }
        `}
`;

export const SmallDesktopVhc = styled.div<{ isMotors?: boolean; isDesktop: boolean }>`
    ${({ isMotors, isDesktop }) =>
        isMotors && isDesktop
            ? css`
                  ${mediaQuery.from(breakpoints.medium)} {
                      display: none;
                  }
              `
            : css`
                  display: none;
              `}
`;

export const VhcPanelContainer = styled.div<Partial<IsMobileCardsLayout>>`
    margin: ${gutterSizes.xlarge} 0;

    ${({ isMobileCardsLayout }) =>
        isMobileCardsLayout &&
        css`
            ${newLayout}
            padding: 0;

            .box {
                border: none;
            }
        `}
`;

export const StaySafeContainer = styled.div<{ isMobileCardsLayout?: boolean }>`
    ${({ isMobileCardsLayout }) =>
        isMobileCardsLayout &&
        css`
            ${newLayout}

            .text-carousel {
                padding: ${gutterSizes.base};
                border: none;
            }
        `}
`;

export const SimilarAdsCarouselContainer = styled.div<IsMobileCardsLayout>`
    ${({ isMobileCardsLayout }) => isMobileCardsLayout && newLayout}
`;

export const VipCarouselContainer = styled(Grid)<IsMobileCardsLayout>`
    ${mediaQuery.until(breakpoints.small)} {
        .carousel-controls-container {
            display: none;
        }
    }

    ${({ isMobileCardsLayout }) =>
        isMobileCardsLayout &&
        css`
            padding: 0;
        `}

    ${({ isMobileCardsLayout }) =>
        !isMobileCardsLayout &&
        css`
            ${mediaQuery.from(breakpoints.large)} {
                float: left;
                padding-right: ${gutterSizes.medium};
            }
        `}
`;

export const VipAdTitle = styled.div<Partial<IsMobileCardsLayout>>`
    & .tabs .tabs-title {
        margin-bottom: 0;
    }

    ${({ isMobileCardsLayout }) => isMobileCardsLayout && newLayout}
`;

export const VipAdMap = styled(VipAdTitle)<IsMobileCardsLayout>`
    width: 100%;

    ${({ isMobileCardsLayout }) =>
        isMobileCardsLayout &&
        css`
            padding: 0;
            margin: 0;
        `}
`;

export const StickyFooter = styled.div<{ isMCUFeed?: boolean }>`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: ${colors.white};
    text-align: center;
    border-top: 1px solid ${colors.bark20};
    z-index: 80;

    ${({ isMCUFeed }) =>
        isMCUFeed &&
        css`
            bottom: auto;
            top: 0;
        `}

    label[for="close-btn-input"] {
        width: 100%;

        > .icn-chevron-d {
            display: block;
            margin: -5px;
        }

        > .icn-chevron-u {
            display: none;
            margin: -5px;
        }
    }

    #close-btn-input:checked {
        + label[for="close-btn-input"] {
            .icn-chevron-d {
                display: none;
            }

            .icn-chevron-u {
                display: block;
            }
        }

        ~ #floatingFooter {
            display: none;
        }
    }
`;

export const CompactPartnershipAdsContainer = styled.div<Partial<IsMobileCardsLayout>>`
    display: none;
    padding-bottom: 5px;
    justify-content: space-between;
    align-items: center;

    ${({ isMobileCardsLayout }) => isMobileCardsLayout && newLayout}

    > iframe,
        > div {
        flex-grow: 1; /* Allows the child to grow and fill the space */
        flex-basis: 0; /* Starts with 0 width and grows as needed */
        width: 50%; /* Each child takes up 50% width when there are two */
    }
`;

export const DefaultPartnershipAdsContainer = styled.div<Partial<IsMobileCardsLayout>>`
    display: none;

    ${({ isMobileCardsLayout }) => isMobileCardsLayout && newLayout}
`;

export const DesktopIframeTopBorder: React.CSSProperties = {
    borderTop: `1px solid ${colors.bark20}`,
};

export const SlotsWrapper = styled.div`
    display: flex;

    & > div {
        flex: 1;
    }
`;

export const VipExpandableList = styled.div`
    padding-top: ${gutterSizes.large18};
    margin-bottom: ${gutterSizes.large18};

    h2 {
        font-size: ${fontSizes.pgLarge};
    }
    
    ${mediaQuery.until(breakpoints.large)} {
        padding-top: ${gutterSizes.medium10};
        margin-bottom: ${gutterSizes.medium10};
    
        h2 {
            font-size: ${fontSizes.xlarge};
        }
    }
`
export const BreadcrumbsWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;

    & > div {
        flex: 1;
    }
`;
